import { Component } from "@theme/component";
import { ThemeEvents, VariantUpdateEvent, ModelInteractionEvent } from "@theme/events";

/**
 * A Shopify CDN image URL at the given width.
 *
 * Shopify's URLs already carry a query string (?v=...), so the width has to be
 * set as a parameter rather than appended: `${url}?width=1600` produced
 * "x.jpg?v=1793094075?width=1600", which the CDN cannot read. It served the
 * full original, about 2.7x the bytes, and never the file the hover had warmed,
 * so every colour change waited on the network.
 *
 * @param {string} url - With or without a ?v= query; protocol-relative is fine.
 * @param {number} width
 * @returns {string}
 */
function sizedImageUrl(url, width) {
  const sized = new URL(url, window.location.href);
  sized.searchParams.set("width", String(width));
  return sized.toString();
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    this.handleCarouselChange();
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.modelInteraction, this.#handleModelInteraction, {
      signal,
    });

    // Add listener directly on the media-gallery element
    this.addEventListener("media:switch-optimistic", this.#switchMediaOptimistic, {
      signal,
    });

    this.#setupZoomReveal();
    this.#scheduleDeferredLoad();
    this.#setupSwatchWarming(target);
  }

  #controller = new AbortController();

  /** @type {Promise<{ openPswp: Function }> | null} */
  #zoomDialogModule = null;

  /** @type {IntersectionObserver | null} */
  #zoomRevealObserver = null;

  /** @type {number | null} */
  #deferredLoadTimer = null;

  /** @type {number} Guards against a slow earlier hero swap landing last. */
  #heroSwapToken = 0;

  /** Number of slides to move from <template> to DOM per animation frame */
  static DEFERRED_BATCH_SIZE = 20;

  updatedCallback() {
    super.updatedCallback?.();
    this.handleCarouselChange();
    this.#setupZoomReveal();
    this.#scheduleDeferredLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#zoomRevealObserver?.disconnect();
    this.#zoomRevealObserver = null;

    if (this.#deferredLoadTimer) {
      clearTimeout(this.#deferredLoadTimer);
      this.#deferredLoadTimer = null;
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source_html = event.detail.data.html;
    const source_variant = event.detail.resource;
    const isOptimistic = event.detail.data.isOptimistic;
    const isBackgroundSync = event.detail.data.isBackgroundSync;
    const newProduct = event.detail.data.newProduct;

    // Check if hide_variants mode (media structure changes completely)
    const hideVariants = this.hasAttribute("data-hide-variants");

    // hide_variants: #showVariantOnly has already rebuilt the gallery from data
    // in the page, without a network round-trip. Replacing it again with the
    // section HTML would repaint the same thing several hundred milliseconds
    // later — the visible second change this used to produce.
    if (hideVariants && !newProduct) return;

    // Skip replace if optimistic update already handled, UNLESS:
    // 1. hide_variants mode (DOM structure changes completely)
    // 2. newProduct (combine listing - media IDs don't match between products)
    if (!hideVariants && !newProduct && (isOptimistic || isBackgroundSync)) return;

    if (!source_html) return;

    const newMediaGallery = source_html.querySelector("media-gallery");

    if (!newMediaGallery) return;

    // For combine listings (newProduct), always replace even if variant has no featured_media
    // For normal variant switch, only replace if variant has featured_media
    if (!newProduct && !source_variant?.featured_media) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles optimistic media switch from cache (instant update without HTML)
   *
   * @param {CustomEvent} event - The optimistic media switch event
   */
  #switchMediaOptimistic = (event) => {
    // Choosing a colour narrows the gallery to that colour: its ball shot, and
    // its close-up where there is one. Do it here, from data already in the
    // page. Leaving it to the server-rendered replace cost a section fetch —
    // measured at 700-800ms, and paid again on every re-click, because the
    // fetch is not cached.
    if (this.hasAttribute("data-hide-variants")) {
      this.#showVariantOnly(event.detail.variant);
      return;
    }

    const variant = event.detail.variant;

    if (!variant?.featured_media?.id) {
      console.warn("⚠️ No featured media in variant");
      return;
    }

    const mediaId = variant.featured_media.id;

    // Check if we have a carousel (might be destroyed in grid mode on desktop)
    const carousel = this.carousel;
    const hasActiveCarousel = carousel?.swiperInstance && !carousel.swiperInstance.destroyed;

    if (hasActiveCarousel) {
      // CAROUSEL MODE: Use slideTo for instant switch
      // IMPORTANT: Query from Swiper's slides array, not DOM, to handle post-recreate scenarios
      let swiperSlides = Array.from(carousel.swiperInstance.slides || []);
      let targetIndex = swiperSlides.findIndex((slide) => {
        const slideEl = slide instanceof HTMLElement ? slide : null;
        return slideEl?.dataset?.mediaId === String(mediaId);
      });

      // If not found, check if slide is in deferred template and force-load
      if (targetIndex === -1 && this.querySelector("[data-deferred-slides]")) {
        this.#forceLoadAllDeferred();
        // Re-query after loading
        swiperSlides = Array.from(carousel.swiperInstance.slides || []);
        targetIndex = swiperSlides.findIndex((slide) => {
          const slideEl = slide instanceof HTMLElement ? slide : null;
          return slideEl?.dataset?.mediaId === String(mediaId);
        });
      }

      if (targetIndex !== -1 && targetIndex !== carousel.swiperInstance.activeIndex) {
        carousel.swiperInstance.slideTo(targetIndex, 0, false); // instant, no animation, no callbacks

        // Update carousel thumbnails active state if they exist
        this.#updateCarouselThumbnailsActive(mediaId);
      } else if (targetIndex === -1) {
        console.warn("⚠️ Target slide not found in Swiper slides");
        // Fallback: try DOM query as last resort
        const domSlides = Array.from(
          this.querySelectorAll(".media-gallery__carousel-wrapper .swiper-slide[data-media-id]")
        );
        const fallbackIndex = domSlides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (fallbackIndex !== -1) {
          carousel.swiperInstance.update(); // Force Swiper to re-scan DOM
          carousel.swiperInstance.slideTo(fallbackIndex, 0, false);
        }
      }
    } else {
      // GRID MODE: Re-order DOM to move selected media to first position
      const mainWrapper = this.querySelector(".media-gallery__carousel-wrapper");
      if (!mainWrapper) {
        console.warn("⚠️ Main wrapper not found");
        return;
      }

      let targetMedia = mainWrapper.querySelector(`[data-media-id="${mediaId}"]`);

      // If not found, check if slide is in deferred template and force-load
      if (!targetMedia && this.querySelector("[data-deferred-slides]")) {
        this.#forceLoadAllDeferred();
        targetMedia = mainWrapper.querySelector(`[data-media-id="${mediaId}"]`);
      }

      if (!targetMedia) {
        console.warn("⚠️ Target media not found");
        return;
      }

      // Move main media to first position if not already there
      let wasReordered = false;
      if (targetMedia !== mainWrapper.firstElementChild) {
        mainWrapper.insertBefore(targetMedia, mainWrapper.firstElementChild);
        wasReordered = true;
      }

      // Handle grid thumbnails (sidebar) if they exist
      const gridThumbs = this.querySelector(".media-gallery__grid-thumbnails");
      if (gridThumbs) {
        const targetThumb = gridThumbs.querySelector(`[data-media-id="${mediaId}"]`);
        if (targetThumb && targetThumb !== gridThumbs.firstElementChild) {
          gridThumbs.insertBefore(targetThumb, gridThumbs.firstElementChild);
        }

        // Update active state for grid thumbnails
        this.#updateGridThumbnailsActive(mediaId);
      }

      // Only scroll into view if DOM was reordered or element is not in viewport
      if (wasReordered) {
        const rect = targetMedia.getBoundingClientRect();
        const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;

        // Only scroll if not fully visible in viewport
        if (!isInViewport) {
          targetMedia.scrollIntoView({ behavior: "instant", block: "start" });
        }
      }
    }
  };

  /** @type {Set<string>} Variants already warmed, so a hover sweep costs one request each. */
  #warmedVariants = new Set();

  /**
   * Show only the chosen shade: its ball shot, and its close-up where there is
   * one. Slides are mutated in place and cloned from the first, so they keep
   * whatever markup the theme rendered — the zoom-reveal wrapper, the ratio box,
   * the classes Swiper looks for.
   *
   * @param {Object} variant
   */
  #showVariantOnly = (variant) => {
    const preview = variant?.featured_media?.preview_image;
    // Shades whose picture is in Files rather than product media -- see
    // #variantMediaMap. Only consulted when there is no featured media, so a
    // product using the ordinary route is unaffected.
    const fromFiles = preview?.src ? null : this.#variantMediaMap().get(String(variant?.id));
    if (!preview?.src && !fromFiles) return;

    const wrapper = this.querySelector(".media-gallery__carousel-wrapper");
    if (!wrapper) return;
    const template = wrapper.querySelector(".media-gallery__item");
    if (!template) return;

    const absolute = (u) => (u.startsWith("//") ? `https:${u}` : u).split(/[?&]width=/)[0];
    const heroRatio = preview?.width && preview?.height ? preview.width / preview.height : null;

    const wanted = [
      preview?.src
        ? { url: absolute(preview.src), alt: variant.featured_media.alt || "", id: variant.featured_media.id, ratio: heroRatio }
        : // No media id exists for these; the slide only needs a stable key.
          { url: absolute(fromFiles.url), alt: fromFiles.alt, id: `variant-${variant.id}`, ratio: fromFiles.ratio },
    ];
    for (const detail of this.#detailMediaMap().get(String(variant.id)) ?? []) {
      wanted.push({ url: detail.url, alt: detail.alt, id: detail.mediaId, ratio: detail.ratio });
    }

    const widths = [...(template.querySelector("img")?.getAttribute("srcset") || "").matchAll(/(\d+)w/g)]
      .map((m) => parseInt(m[1], 10));
    const list = widths.length ? widths : [400, 600, 800, 1000, 1200, 1500, 1800];
    // The width the browser has already chosen for this slot, so the copy that
    // gets loaded below is the same one the <img> will end up displaying.
    const current = Number((template.querySelector("img")?.currentSrc || "").match(/[?&]width=(\d+)/)?.[1]);
    const width = current >= 400 ? current : 1200;

    const token = ++this.#heroSwapToken;

    // Fetch the new pictures now. Hovering has usually done this already, in
    // which case they are complete on the spot and the swap below happens in
    // the same tick as the click.
    const preloads = wanted.map((want) => {
      const pre = new Image();
      pre.src = sizedImageUrl(want.url, width);
      return pre;
    });

    // Put the new pictures on screen together and animate them in.
    //
    // The reveal is driven here rather than by toggling .is-revealed off and on.
    // A CSS transition only runs when the browser has a previous computed style
    // to move from, which a slide cloned in this same tick does not — so a shade
    // with a close-up animated its first picture and not its second, and the
    // effect appeared to come and go depending on which slides were reused.
    // These keyframes mirror .media-zoom-reveal in theme.css exactly, and run
    // whether the slide is new or reused.
    const REVEAL_SCALE = [{ transform: "scale(1.15)" }, { transform: "none" }];
    const REVEAL_FADE = [{ opacity: 0 }, { opacity: 1 }];
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    // Start a slide's reveal only once its new picture can paint. Assigning src
    // does not replace the picture on screen until the new one has arrived, so
    // a reveal started any earlier plays over the previous colour: a flash of
    // the old image, which then sits there until the new one pops in. When the
    // pictures were already loaded they paint in the next frame, so the reveal
    // starts straight away; otherwise each waits for its own picture.
    /** @param {HTMLImageElement} img @param {boolean} ready */
    const reveal = (img, ready) => {
      const play = () => {
        if (token !== this.#heroSwapToken) return;
        // Clicking through shades otherwise leaves every previous reveal
        // attached to the slide, stacking up on each swap.
        img.getAnimations?.().forEach((a) => a.cancel());
        img.animate(REVEAL_SCALE, { duration: 1300, easing: "cubic-bezier(0.16, 1, 0.3, 1)" });
        img.animate(REVEAL_FADE, { duration: 500, easing: "ease-in-out" });
      };
      if (ready) play();
      else img.decode().then(play, play);
    };

    const commit = () => {
      if (token !== this.#heroSwapToken) return;
      const ready = preloads.every((pre) => pre.complete && pre.naturalWidth > 0);

      const items = [...wrapper.querySelectorAll(".media-gallery__item")];
      const shown = [];

      wanted.forEach((want, n) => {
        let item = items[n];
        if (!item) {
          item = template.cloneNode(true);
          (items[n - 1] ?? template).after(item);
        }
        // Keep the resting state visible; the animation below supplies the
        // movement. Without this a slide left un-revealed sits at opacity 0.
        item.classList.add("is-revealed");

        const img = item.querySelector("img");
        if (img) {
          img.srcset = list.map((w) => `${sizedImageUrl(want.url, w)} ${w}w`).join(", ");
          img.src = sizedImageUrl(want.url, width);
          img.alt = want.alt;
          img.removeAttribute("loading");
        }
        if (item instanceof HTMLElement) {
          item.dataset.mediaId = String(want.id ?? "");
          item.dataset.mediaSrc = want.url;
          // The box is sized from --ratio before the image paints; a stale value
          // on a reused slide makes the gallery jump.
          if (want.ratio) {
            item.style.setProperty("--ratio", String(want.ratio));
            item.style.setProperty("--ratio-mobile", String(want.ratio));
          }
          item.classList.remove("loading");
          shown.push(item);
        }
      });

      // Whatever is left belonged to the previous colour, or to the hero and
      // hover pair shown before any colour was chosen.
      items.slice(wanted.length).forEach((extra) => extra.remove());

      // When the pictures were ready every slide starts its reveal in the same
      // frame, so a pair moves as one.
      if (!reduceMotion) {
        for (const item of shown) {
          const img = item.querySelector("img");
          if (typeof img?.animate !== "function") continue;
          reveal(img, ready);
        }
      }

      // Retire the thumbnail strip. It is built from the product's own media
      // when the page renders and knows nothing about the rebuild above, so
      // after a colour is chosen it went on offering the three general
      // photographs -- the first still ringed as current -- directly over a
      // picture of the shade the shopper had just picked.
      //
      // Dropped rather than rebuilt: what is left is that colour's own set --
      // one picture for a yarn shade, a pair where there is a close-up, a run
      // of them for a leather bag -- and the carousel keeps its own arrows,
      // dots and counter for the set. The strip is the mobile-only
      // "--inside" overlay; nothing sets display on it at that width, so the
      // hidden attribute is enough.
      this.querySelector(".media-gallery__carousel-thumbnails")?.setAttribute("hidden", "");

      const carousel = this.carousel;
      if (carousel?.swiperInstance && !carousel.swiperInstance.destroyed) {
        carousel.swiperInstance.update();
        carousel.swiperInstance.slideTo(0, 0, false);
      }
    };

    // Already loaded (the usual case after a hover): swap now, in the click's
    // own tick. Waiting on decode() here instead cost up to a second with every
    // byte in cache (d158c70), and is what made the click look ignored.
    if (preloads.every((pre) => pre.complete && pre.naturalWidth > 0)) {
      commit();
      return;
    }

    // Not loaded yet: keep the previous colour up until every new picture has
    // arrived, so a shade with a close-up changes as one rather than one
    // picture, a gap, then the other. Load, not decode: decoding off-DOM first
    // is the slow path measured above.
    let settled = false;
    const once = () => {
      if (settled || token !== this.#heroSwapToken) return;
      settled = true;
      clearTimeout(timer);
      commit();
    };
    // Ceiling, only for an image that never arrives. Past this the swap goes
    // ahead, and reveal() holds each slide's animation until its own picture
    // lands, so the reveal never plays over the old colour. It was 800ms, but
    // a cold pair often takes about that long, and swapping before both arrive
    // blanked the main picture and revealed the close-up first. The swatch,
    // colour name and price already answer the click straight away.
    const timer = setTimeout(once, 2500);

    let pending = preloads.length;
    for (const pre of preloads) {
      const done = () => --pending === 0 && once();
      if (pre.complete) done();
      else {
        pre.addEventListener("load", done, { once: true });
        pre.addEventListener("error", done, { once: true });
      }
    }
  };

  /**
   * Warm a shade's images while the pointer is still on its swatch.
   *
   * Choosing a colour swaps in server-rendered HTML, so the new <img> elements
   * only paint once their bytes arrive. Requesting those bytes on hover means
   * they are usually already in the browser cache by the time the click lands,
   * and the gallery changes without waiting on the network.
   *
   * Nothing here touches the DOM or the gallery's state — it only asks the
   * browser to fetch, so a mis-warmed URL costs a wasted request and nothing more.
   *
   * @param {Element | null} target - The section to delegate from.
   */
  #setupSwatchWarming = (target) => {
    const root = target ?? this.closest(".shopify-section");
    if (!root) return;

    // pointerenter does not bubble, so delegate on pointerover and dedupe by id.
    root.addEventListener("pointerover", this.#warmFromSwatch, { signal: this.#controller.signal });
    // Keyboard users tab through the same swatches.
    root.addEventListener("focusin", this.#warmFromSwatch, { signal: this.#controller.signal });
    // Touch has no hover at all, so every tap would otherwise start cold.
    // pointerdown fires before the click, which buys the fetch a head start.
    root.addEventListener("pointerdown", this.#warmFromSwatch, { signal: this.#controller.signal });
  };

  /** @param {Event} event */
  #warmFromSwatch = (event) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    const label = el.closest("[data-variant-id], .variant-option__button-label");
    if (!label) return;

    const input = label.matches("[data-variant-id]") ? label : label.querySelector("[data-variant-id]");
    const variantId = input?.getAttribute("data-variant-id");
    if (!variantId || this.#warmedVariants.has(variantId)) return;
    this.#warmedVariants.add(variantId);

    // Ask for the width the gallery will ask for. currentSrc is the candidate the
    // browser already picked for this viewport and density, so warming that URL
    // warms the exact file the click needs. Measuring the rendered box instead
    // looked reasonable but broke whenever the image had not laid out yet — it
    // warmed a thumbnail, and the real request still went to the network.
    // A floor, because currentSrc is only meaningful once the image has laid out
    // and been chosen. Before then it can report a thumbnail, and warming that
    // would leave the real request still to make.
    const shown = this.querySelector(".media-gallery__carousel-wrapper img");
    const chosen = Number((shown?.currentSrc || "").match(/[?&]width=(\d+)/)?.[1]);
    const width = chosen >= 400 ? chosen : 1200;

    for (const url of this.#variantImageUrls(variantId)) {
      const img = new Image();
      img.decoding = "async";
      img.src = sizedImageUrl(url, width);
    }
  };

  /**
   * The ball shot and, where there is one, the close-up — for a shade that is
   * only being hovered, so neither is in the gallery yet.
   *
   * The ball shot comes from the variant picker's own cache of variant data.
   * Not from the swatch's background image, which looks like the same picture
   * but is a separate file: shades can carry a dedicated swatch close-up via
   * custom.swatch_url, so warming that would fetch something the gallery never
   * asks for. The close-up comes from the map the gallery block renders, since
   * nothing else on the page knows about it.
   *
   * @param {string} variantId
   * @returns {string[]}
   */
  #variantImageUrls = (variantId) => {
    const urls = [];

    const featured = this.#variantImageMap().get(variantId);
    if (featured) urls.push(featured);

    for (const detail of this.#detailMediaMap().get(variantId) ?? []) urls.push(detail.url);

    return urls;
  };

  /** @type {Map<string, string> | null} */
  #variantImageCache = null;

  /**
   * variantId -> featured image URL, read from the variant picker's cache.
   * @returns {Map<string, string>}
   */
  #variantImageMap = () => {
    if (this.#variantImageCache) return this.#variantImageCache;
    const map = new Map();
    const raw = this.closest(".shopify-section")?.querySelector("script[data-variants-cache]")?.textContent;
    try {
      for (const variant of JSON.parse(raw || "{}").variants ?? []) {
        const src = variant?.featured_image?.src;
        if (!src) continue;
        map.set(String(variant.id), (src.startsWith("//") ? `https:${src}` : src).split(/[?&]width=/)[0]);
      }
    } catch {
      /* no cache on the page — hover warming simply does nothing */
    }
    this.#variantImageCache = map;
    return map;
  };

  /** @type {Map<string, {mediaId: string, url: string, alt: string, ratio: number | null}[]> | null} */
  #detailMediaCache = null;

  /**
   * variantId -> its close-ups, in product-media order, from the block's
   * "variant|media|url|alt|ratio;;" payload. A yarn shade has one; a leather
   * bag colour has as many as the supplier photographed.
   * @returns {Map<string, {mediaId: string, url: string, alt: string, ratio: number | null}[]>}
   */
  #detailMediaMap = () => {
    if (this.#detailMediaCache) return this.#detailMediaCache;
    const map = new Map();
    const raw = this.querySelector("script[data-detail-media]")?.textContent || "";
    for (const row of raw.split(";;")) {
      const [variantId, mediaId, url, alt, ratio] = row.trim().split("|");
      if (!variantId || !url) continue;
      if (!map.has(variantId)) map.set(variantId, []);
      map.get(variantId).push({
        mediaId,
        url: url.split(/[?&]width=/)[0],
        alt: alt || "",
        ratio: Number(ratio) || null,
      });
    }
    this.#detailMediaCache = map;
    return map;
  };

  #variantMediaCache = null;

  /**
   * variantId -> its own picture, for shades whose photos live in Files rather
   * than product media. Shopify caps a product at 250 media and a variant image
   * must come from that list, so a 500-shade product cannot use the usual
   * route; the block emits "variant|url|alt|ratio;;" instead.
   * @returns {Map<string, {url: string, alt: string, ratio: number | null}>}
   */
  #variantMediaMap = () => {
    if (this.#variantMediaCache) return this.#variantMediaCache;
    const map = new Map();
    const raw = this.querySelector("script[data-variant-media]")?.textContent || "";
    for (const row of raw.split(";;")) {
      const [variantId, url, alt, ratio] = row.trim().split("|");
      if (!variantId || !url) continue;
      map.set(variantId, {
        url: url.split(/[?&]width=/)[0],
        alt: alt || "",
        ratio: Number(ratio) || null,
      });
    }
    this.#variantMediaCache = map;
    return map;
  };

  /**
   * hide_variants: swap only the hero (first) media image to the given variant's
   * featured media, without touching the shared images below or refetching HTML.
   * @param {Object} variant
   */
  #swapHeroImage = (variant) => {
    const media = variant?.featured_media;
    const preview = media?.preview_image;
    if (!preview?.src) return;

    const firstItem = this.querySelector(".media-gallery__carousel-wrapper .media-gallery__item");
    const img = firstItem?.querySelector("img");
    if (!img) return;

    let base = preview.src.split("?")[0];
    if (base.startsWith("//")) base = "https:" + base;

    // Reuse the widths already in the img's srcset (fall back to a sensible set)
    const widths = [...(img.getAttribute("srcset") || "").matchAll(/(\d+)w/g)].map((m) => parseInt(m[1], 10));
    const list = widths.length ? widths : [400, 600, 800, 1000, 1200, 1500];
    const largest = Math.max(...list);

    const srcset = list.map((w) => `${base}?width=${w} ${w}w`).join(", ");
    const src = `${base}?width=${largest}`;

    // Clicking through swatches quickly starts several loads at once; only the
    // newest may paint, or a slower earlier image lands last.
    const token = ++this.#heroSwapToken;

    const apply = () => {
      if (token !== this.#heroSwapToken) return;

      img.srcset = srcset;
      img.src = src;
      if (media.alt) img.alt = media.alt;
      if (firstItem instanceof HTMLElement) firstItem.dataset.mediaId = String(media.id);

      // Fade only now. Assigning src does not paint the new image until it has
      // downloaded and decoded, so animating up front just faded the outgoing
      // image and left the real swap to pop in unannounced.
      if (typeof img.animate === "function") {
        img.animate([{ opacity: 0.25 }, { opacity: 1 }], { duration: 280, easing: "ease-out" });
      }
    };

    // Warm one concrete URL rather than a srcset. A detached image given a
    // srcset has no layout to choose a candidate from, so with no usable
    // `sizes` it may never start loading and never fire load or error.
    const rendered = Math.round((img.getBoundingClientRect().width || largest) * (window.devicePixelRatio || 1));
    const best = list.find((w) => w >= rendered) ?? largest;

    const preloader = new Image();
    preloader.src = `${base}?width=${best}`;

    // Swap as soon as the bytes land, but never leave a stale hero: if the
    // network stalls, go ahead anyway rather than stranding the old colour.
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      apply();
    };
    const timer = setTimeout(settle, 800);

    if (preloader.complete) {
      settle();
    } else {
      preloader.addEventListener("load", settle, { once: true });
      preloader.addEventListener("error", settle, { once: true });
    }
  };

  /**
   * Update active state for carousel thumbnails
   * @param {string|number} mediaId
   */
  #updateCarouselThumbnailsActive = (mediaId) => {
    const carouselThumbs = this.querySelector(".media-gallery__carousel-thumbnails");
    if (!carouselThumbs) return;

    const thumbItems = carouselThumbs.querySelectorAll("[data-media-id]");

    let foundActive = false;
    for (const thumb of thumbItems) {
      const thumbMediaId = thumb.dataset.mediaId;
      const isMatch = String(thumbMediaId) === String(mediaId);

      if (isMatch) {
        thumb.classList.add("is-active");
        foundActive = true;
      } else {
        thumb.classList.remove("is-active");
      }
    }

    if (!foundActive) {
      console.warn("⚠️ No matching carousel thumbnail found for mediaId:", mediaId);
    }
  };

  /**
   * Update active state for grid thumbnails
   * @param {string|number} mediaId
   */
  #updateGridThumbnailsActive = (mediaId) => {
    const gridThumbs = this.querySelector(".media-gallery__grid-thumbnails");
    if (!gridThumbs) return;

    const thumbItems = gridThumbs.querySelectorAll("[data-media-id]");

    let foundActive = false;
    for (const thumb of thumbItems) {
      const thumbMediaId = thumb.dataset.mediaId;
      const isMatch = String(thumbMediaId) === String(mediaId);

      if (isMatch) {
        thumb.classList.add("is-active");
        thumb.setAttribute("aria-current", "true");
        foundActive = true;
      } else {
        thumb.classList.remove("is-active");
        thumb.removeAttribute("aria-current");
      }
    }

    if (!foundActive) {
      console.warn("⚠️ No matching grid thumbnail found for mediaId:", mediaId);
    }
  };

  /**
   * Handle 3D model interaction - toggle carousel drag
   * @param {ModelInteractionEvent} event
   */
  #handleModelInteraction = (event) => {
    const { isInteracting } = event.detail;

    // isInteracting = true (playing) -> disable drag
    // isInteracting = false (paused) -> enable drag
    this.toggleCarouselDrag(!isInteracting);
  };

  #realIndexChangeHandler = (swiper) => {
    const { slides, activeIndex } = swiper;
    const slide = slides[activeIndex];
    if (!slide) return;
    const mediaType = slide.dataset.mediaType;
    const mediaId = slide.dataset.mediaId;
    const modelViewer = slide.querySelector("model-viewer");
    const modelViewerButton = slide.querySelector(".shopify-model-viewer-ui__button");
    const shopifyXrButton = this.querySelector("button[data-shopify-xr]");

    if (mediaType === "model") {
      if (slide.dataset?.playing === "true") {
        this.toggleCarouselDrag(false);
      } else {
        this.toggleCarouselDrag(true);
      }
      if (modelViewer?.classList.contains("shopify-model-viewer-ui__disabled")) {
        modelViewerButton?.removeAttribute("hidden");
      } else {
        modelViewerButton?.setAttribute("hidden", "");
      }
      if (shopifyXrButton) {
        shopifyXrButton.dataset.shopifyModel3dId = mediaId;
        shopifyXrButton.classList.remove("hidden");
      }
    } else {
      this.toggleCarouselDrag(true);
      shopifyXrButton?.classList.add("hidden");
    }
  };

  handleCarouselChange = () => {
    if (!this.carousel?.swiperInstance) return;
    this.carousel.swiperInstance.off("realIndexChange", this.#realIndexChangeHandler);
    this.carousel.swiperInstance.on("realIndexChange", this.#realIndexChangeHandler);
  };

  toggleCarouselDrag = (state) => {
    if (!this.carousel?.swiperInstance) return;
    this.carousel.swiperInstance.allowTouchMove = state;
  };

  handleGridThumbClick = (event) => {
    const source = event.target.closest("[data-media-id]");
    if (!source) return;
    const mediaId = source.dataset.mediaId;

    // 1) Toggle active state for the clicked thumb
    const container = this.querySelector(".media-gallery__grid-thumbnails");
    const thumbs = container ? Array.from(container.querySelectorAll(".media-gallery__item[data-media-id]")) : [];
    for (const el of thumbs) {
      el.classList.remove("is-active");
      el.removeAttribute("aria-current");
    }
    const activeThumb = container?.querySelector(`.media-gallery__item[data-media-id="${mediaId}"]`);
    activeThumb?.classList.add("is-active");
    activeThumb?.setAttribute("aria-current", "true");

    // 2) Scroll the wrapper by one "step" if the click is near the bottom/top edge
    if (container && activeThumb) {
      requestAnimationFrame(() => {
        const rowGap = parseFloat(getComputedStyle(container).rowGap || "0") || 0;
        const step = Math.ceil(activeThumb.offsetHeight + rowGap);

        const viewportTop = container.scrollTop;
        const viewportBottom = viewportTop + container.clientHeight;

        const itemTop = activeThumb.offsetTop;
        const itemBottom = itemTop + activeThumb.offsetHeight;

        const idx = thumbs.indexOf(activeThumb);
        const next = thumbs[Math.min(idx + 1, thumbs.length - 1)];
        const prev = thumbs[Math.max(idx - 1, 0)];

        const nearBottom = viewportBottom - itemBottom <= step;
        const nearTop = itemTop - viewportTop <= step;

        if (nearBottom && next) {
          next.scrollIntoView({ block: "nearest", behavior: "smooth" });
          // Alternatively: container.scrollBy({ top: step, behavior: "smooth" });
        }

        if (nearTop && prev) {
          prev.scrollIntoView({ block: "nearest", behavior: "smooth" });
          // Alternatively: container.scrollBy({ top: -step, behavior: "smooth" });
        }
      });
    }

    // 3) Scroll to the corresponding main media item
    const target = this.querySelector(`.media-gallery__carousel-wrapper [data-media-id="${mediaId}"]`);

    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /**
   * Preloads the zoom dialog module on hover for better UX.
   */
  preloadZoomDialog = () => {
    // Only preload once
    if (this.#zoomDialogModule) return;

    // Start preloading the module
    this.#zoomDialogModule = import("@theme/zoom-dialog");
  };

  /**
   * Opens the zoom dialog with loading state management.
   */
  openZoomDialog = async (event) => {
    // Find the button - could be event.target or a parent
    const trigger =
      event.target instanceof HTMLButtonElement
        ? event.target
        : /** @type {HTMLElement} */ (event.target.closest("button"));
    if (!trigger) return;

    const clickedId = trigger?.getAttribute("data-media-id") ?? null;

    // Show loading state
    this.#setZoomButtonLoading(trigger, true);

    try {
      const mediaNodes = Array.from(
        this.querySelectorAll('.media-gallery__carousel-wrapper .media-gallery__item[data-media-type="image"]')
      );
      const items = mediaNodes.map((media) => {
        return {
          src: media.dataset.mediaSrc,
          width: media.dataset.mediaWidth,
          height: media.dataset.mediaHeight,
          element: media,
        };
      });

      const startIndex = Math.max(
        0,
        mediaNodes.findIndex((media) => media?.dataset.mediaId === clickedId)
      );

      // Use preloaded module if available, otherwise import
      const zoomDialogModule = this.#zoomDialogModule || import("@theme/zoom-dialog");
      this.#zoomDialogModule = zoomDialogModule; // Cache for future use

      const { openPswp } = await zoomDialogModule;
      openPswp(items, startIndex, {
        trigger,
        onChange: (pswpIndex) => {
          const swiper = this.carousel?.swiperInstance;
          if (!swiper) return;
          // Map pswpIndex to slide index in Swiper; items and mediaNodes are aligned
          swiper.slideTo(pswpIndex, 0, false);
        },
      });
    } catch (error) {
      console.error("Failed to open zoom dialog:", error);
    } finally {
      // Hide loading state
      this.#setZoomButtonLoading(trigger, false);
    }
  };

  /**
   * Sets loading state for zoom dialog button.
   *
   * @param {HTMLElement} button - The zoom dialog button
   * @param {boolean} isLoading - Whether the button is in loading state
   */
  #setZoomButtonLoading(button, isLoading) {
    const spinner = button.querySelector('[ref="zoomDialogSpinner"]');
    if (!spinner) return;

    if (isLoading) {
      button.classList.add("btn--loading");
      spinner.classList.remove("hidden");
    } else {
      button.classList.remove("btn--loading");
      spinner.classList.add("hidden");
    }
  }

  // ---------------------------------------------------------------------------
  // Deferred slide loading
  // ---------------------------------------------------------------------------

  /**
   * Schedule loading of deferred slides from <template> elements.
   * Slides beyond the initial set are rendered inside inert <template> tags
   * (no custom-element lifecycle, no Swiper overhead). After the page is
   * interactive we progressively move them into the live DOM in batches.
   */
  #scheduleDeferredLoad() {
    // Cancel any pending timer (e.g. from a previous updatedCallback)
    if (this.#deferredLoadTimer) {
      clearTimeout(this.#deferredLoadTimer);
      this.#deferredLoadTimer = null;
    }

    if (!this.querySelector("[data-deferred-slides]")) return;

    this.#deferredLoadTimer = setTimeout(() => {
      this.#deferredLoadTimer = null;
      this.#loadDeferredSlides();
    }, 1500);
  }

  /**
   * Progressively move deferred slides from <template> into the live DOM.
   * Processes DEFERRED_BATCH_SIZE slides per animation frame to avoid jank.
   */
  #loadDeferredSlides() {
    const slideTemplate = this.querySelector("[data-deferred-slides]");
    const thumbTemplate = this.querySelector("[data-deferred-thumbnails]");

    if (!slideTemplate && !thumbTemplate) return;

    const mainWrapper = this.querySelector(".media-gallery__carousel-wrapper");
    const thumbWrapper = this.querySelector(".carousel__thumbnails-swiper > .swiper-wrapper");
    const batchSize = MediaGallery.DEFERRED_BATCH_SIZE;

    const loadBatch = () => {
      let hasMore = false;

      // --- Main slides batch ---
      if (slideTemplate?.parentNode && mainWrapper) {
        const slides = slideTemplate.content.children;
        const count = Math.min(batchSize, slides.length);
        for (let i = 0; i < count; i++) {
          mainWrapper.appendChild(slides[0]); // [0] shifts as children are moved
        }
        if (slides.length === 0) {
          slideTemplate.remove();
        } else {
          hasMore = true;
        }
      }

      // --- Thumbnail slides batch ---
      if (thumbTemplate?.parentNode && thumbWrapper) {
        const thumbs = thumbTemplate.content.children;
        const count = Math.min(batchSize, thumbs.length);
        for (let i = 0; i < count; i++) {
          thumbWrapper.appendChild(thumbs[0]);
        }
        if (thumbs.length === 0) {
          thumbTemplate.remove();
        } else {
          hasMore = true;
        }
      }

      if (hasMore) {
        requestAnimationFrame(loadBatch);
      } else {
        // All deferred slides loaded — update Swipers & zoom reveal
        const carousel = this.carousel;
        if (carousel?.swiperInstance && !carousel.swiperInstance.destroyed) {
          carousel.swiperInstance.update();
        }
        if (carousel?.thumbnailSwiper && !carousel.thumbnailSwiper.destroyed) {
          carousel.thumbnailSwiper.update();
        }
        this.#setupZoomReveal();
      }
    };

    requestAnimationFrame(loadBatch);
  }

  /**
   * Immediately load ALL deferred slides (synchronous, no batching).
   * Used when a variant change needs a slide that's still in the <template>.
   * A brief jank is acceptable because this is a user-initiated action.
   */
  #forceLoadAllDeferred() {
    const slideTemplate = this.querySelector("[data-deferred-slides]");
    const thumbTemplate = this.querySelector("[data-deferred-thumbnails]");

    if (!slideTemplate && !thumbTemplate) return;

    // Cancel scheduled progressive load — we're loading everything now
    if (this.#deferredLoadTimer) {
      clearTimeout(this.#deferredLoadTimer);
      this.#deferredLoadTimer = null;
    }

    // Move ALL main slides at once
    if (slideTemplate) {
      const mainWrapper = this.querySelector(".media-gallery__carousel-wrapper");
      if (mainWrapper) {
        while (slideTemplate.content.firstChild) {
          mainWrapper.appendChild(slideTemplate.content.firstChild);
        }
      }
      slideTemplate.remove();
    }

    // Move ALL thumbnail slides at once
    if (thumbTemplate) {
      const thumbWrapper = this.querySelector(".carousel__thumbnails-swiper > .swiper-wrapper");
      if (thumbWrapper) {
        while (thumbTemplate.content.firstChild) {
          thumbWrapper.appendChild(thumbTemplate.content.firstChild);
        }
      }
      thumbTemplate.remove();
    }

    // Update Swipers so they know about the new slides
    const carousel = this.carousel;
    if (carousel?.swiperInstance && !carousel.swiperInstance.destroyed) {
      carousel.swiperInstance.update();
    }
    if (carousel?.thumbnailSwiper && !carousel.thumbnailSwiper.destroyed) {
      carousel.thumbnailSwiper.update();
    }

    this.#setupZoomReveal();
  }

  // ---------------------------------------------------------------------------
  // Zoom reveal (CSS-only zoom-out)
  // ---------------------------------------------------------------------------

  /**
   * CSS-only zoom-out reveal for product media.
   * Replaces per-element <motion-component> with a single shared IntersectionObserver.
   * When a .media-zoom-reveal element enters viewport and its image loads,
   * adds .is-revealed → CSS transition handles the zoom-out animation.
   */
  #setupZoomReveal() {
    this.#zoomRevealObserver?.disconnect();

    const items = this.querySelectorAll(".media-zoom-reveal:not(.is-revealed)");
    if (!items.length) return;

    this.#zoomRevealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#revealItem(entry.target);
            this.#zoomRevealObserver?.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    items.forEach((item) => this.#zoomRevealObserver.observe(item));
  }

  /**
   * Reveal a single media item after its image loads (or immediately if already loaded).
   * @param {Element} element
   */
  #revealItem(element) {
    const img = element.querySelector("img");

    // If no image or already loaded, reveal immediately
    if (!img || img.complete) {
      element.classList.add("is-revealed");
      return;
    }

    const reveal = () => element.classList.add("is-revealed");
    img.addEventListener("load", reveal, { once: true });
    img.addEventListener("error", reveal, { once: true });

    // Safety timeout — don't block forever if image fails silently
    setTimeout(() => {
      if (!element.classList.contains("is-revealed")) {
        element.classList.add("is-revealed");
      }
    }, 5000);
  }

  get carousel() {
    return this.refs.carousel;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
